import { Box, Typography, useTheme } from "@mui/material";
import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import type { WorkflowMetricsChartRow } from "../../utils/crmOperationalDashboard.aggregate";

const TooltipContent = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "8px",
        padding: "11px 18px 12px",
        border: "1px solid #f0f0f0",
        boxShadow: "0px 12px 25px 0px rgb(0 0 0 / 12%)",
        maxWidth: 220,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "#403e57",
          marginBottom: "8px",
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "uppercase",
          display: "block",
        }}
      >
        {label}
      </Typography>
      {payload.map((entry: any, i: number) => (
        <Box key={i} display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box sx={{ width: 10, height: 10, backgroundColor: entry.color, borderRadius: "2px" }} />
          <Typography
            variant="caption"
            sx={{
              color: "#5e5d72",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {entry.name}: {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export type WorkflowMetricsChartProps = {
  data: WorkflowMetricsChartRow[];
  loading?: boolean;
};

export const WorkflowMetricsChart = ({ data, loading }: WorkflowMetricsChartProps) => {
  const theme = useTheme();
  const hasData = data.length > 0;

  return (
    <div
      style={{
        width: "100%",
        height: 410,
        background: "#fff",
        borderRadius: 16,
        padding: 16,
      }}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Tasks by Lead Status
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Tasks in pipeline grouped by lead status and task status
        </Typography>
      </Box>
      {loading ? (
        <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : !hasData ? (
        <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No tasks in scope
          </Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: 5,
            }}
          >
            <defs>
              <linearGradient id="gradientTodo" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.9} />
                <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.4} />
              </linearGradient>
              <linearGradient id="gradientInprogress" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.palette.warning.main} stopOpacity={0.9} />
                <stop offset="100%" stopColor={theme.palette.warning.main} stopOpacity={0.4} />
              </linearGradient>
              <linearGradient id="gradientDone" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.palette.success.main} stopOpacity={0.9} />
                <stop offset="100%" stopColor={theme.palette.success.main} stopOpacity={0.4} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />

            <XAxis
              dataKey="name"
              angle={-90}
              textAnchor="end"
              interval={0}
              height={100}
              tick={{
                fill: "#6B7280",
                fontSize: 12,
                fontWeight: 500,
              }}
              axisLine={false}
              tickLine={false}
            />

            <YAxis
              allowDecimals={false}
              tick={{
                fill: "#6B7280",
                fontSize: 12,
              }}
              axisLine={false}
              tickLine={false}
            />

            <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} content={<TooltipContent />} />

            <Bar dataKey="todo" name="Todo" fill="url(#gradientTodo)" radius={[4, 4, 0, 0]} barSize={20} />
            <Bar dataKey="inprogress" name="In Progress" fill="url(#gradientInprogress)" radius={[4, 4, 0, 0]} barSize={20} />
            <Bar dataKey="done" name="Done" fill="url(#gradientDone)" radius={[4, 4, 0, 0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
